import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { API_URL } from "@/config/env";
import { toast } from "react-hot-toast";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { getMenuUrl, hasValidTableSession } from "@/utils/menu";
import restoLogoPequeno from "@/assets/images/resto-logo-pequeño.png";

const PaymentPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOrders, setSelectedOrders] = useState({});
  const [totalToPay, setTotalToPay] = useState(0);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [menuUrl, setMenuUrl] = useState("/");

  useEffect(() => {
    const url = getMenuUrl();
    setMenuUrl(url);
  }, []);

  const validateTableSession = () => {
    if (!hasValidTableSession()) {
      toast.error("Sesión de mesa no válida");
      navigate(menuUrl);
      return false;
    }
    return true;
  };

  const fetchOrders = async () => {
    try {
      if (!validateTableSession()) return;

      setLoading(true);
      setError(null);
      const mesaId = localStorage.getItem("mesaId");

      const response = await axios.post(`${API_URL}/api/order/userorders`, {
        mesaId: mesaId,
      });

      if (!response.data?.data) {
        throw new Error("No se encontraron órdenes");
      }

      // Filtramos las órdenes que están en preparación o con pago solicitado
      const validOrders = response.data.data.filter(
        (order) =>
          order.status === "En Preparacion" || order.status === "PagoSolicitado"
      );

      setOrders(validOrders);
    } catch (err) {
      console.error("Error fetching orders:", err);
      setError(err.message || "Error al cargar las órdenes");
      toast.error("Error al cargar las órdenes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const isOrderPayable = (order) => {
    return order.status !== "PagoSolicitado";
  };

  const handleOrderSelection = (orderId, isChecked) => {
    const order = orders.find((o) => o._id === orderId);
    if (!order || !isOrderPayable(order)) return;

    setSelectedOrders((prev) => ({
      ...prev,
      [orderId]: isChecked,
    }));

    if (order) {
      setTotalToPay((prev) =>
        isChecked ? prev + order.amount : prev - order.amount
      );
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  const handlePayment = async (paymentType) => {
    if (processingPayment) return;

    try {
      setProcessingPayment(true);
      const mesaId = localStorage.getItem("mesaId");
      const selectedOrderIds = Object.entries(selectedOrders)
        .filter(([_, isSelected]) => isSelected)
        .map(([orderId]) => orderId);

      if (selectedOrderIds.length === 0) {
        toast.error("Por favor selecciona al menos una orden para pagar");
        return;
      }

      // Procesar el pago para cada orden seleccionada
      const results = await Promise.all(
        selectedOrderIds.map((orderId) =>
          axios.post(`${API_URL}/api/order/verify`, {
            orderId,
            tipoPago: paymentType,
            mesaId,
          })
        )
      );

      const allSuccessful = results.every((result) => result.data?.success);

      if (allSuccessful) {
        toast.success(`Pago con ${paymentType} registrado correctamente`);
        // Actualizar la lista de órdenes
        await fetchOrders();
        // Limpiar selecciones
        setSelectedOrders({});
        setTotalToPay(0);
      } else {
        throw new Error("Algunas órdenes no pudieron ser procesadas");
      }
    } catch (error) {
      console.error("Error processing payment:", error);
      toast.error(error.message || "Error al procesar el pago");
    } finally {
      setProcessingPayment(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando detalles del pedido...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md p-4 flex justify-between items-center">
        <Link to={menuUrl} className="flex-shrink-0">
          <img src={restoLogoPequeno} alt="Tataowi" className="h-6 w-auto" />
        </Link>
        <nav className="flex gap-4">
          <Link
            to="/pedidos"
            className="text-gray-800 hover:text-gray-600 font-medium text-sm"
          >
            MIS PEDIDOS
          </Link>
          <Link to="" className="text-orange-500 font-medium text-sm">
            PAGAR
          </Link>
        </nav>
      </header>

      <div className="mt-20 px-4 max-w-2xl mx-auto w-full pb-32">
        <div className="space-y-4">
          <div className="flex justify-between items-center border-b border-gray-200 pb-4">
            <h1 className="text-xs font-bold text-gray-900">
              RESUMEN DE PEDIDOS
            </h1>
            <span className="text-xs font-bold text-orange-500">PAGO</span>
          </div>

          {orders.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">No hay órdenes pendientes de pago</p>
              <Link
                to={menuUrl}
                className="mt-4 inline-block text-orange-500 hover:text-orange-600 font-medium"
              >
                Volver al menú
              </Link>
            </div>
          ) : (
            <div className="space-y-6">
              {orders.map((order) => (
                <Card
                  key={order._id}
                  className={`w-full ${
                    order.status === "PagoSolicitado" ? "bg-orange-50" : ""
                  }`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <Checkbox
                        id={`order-${order._id}`}
                        checked={
                          order.status === "PagoSolicitado"
                            ? true
                            : selectedOrders[order._id] || false
                        }
                        disabled={!isOrderPayable(order)}
                        onCheckedChange={(checked) =>
                          handleOrderSelection(order._id, checked)
                        }
                        className={`${
                          !isOrderPayable(order)
                            ? "opacity-50 cursor-not-allowed data-[state=checked]:bg-orange-500 data-[state=checked]:text-white"
                            : ""
                        }`}
                      />
                      <div className="flex-1">
                        <div className="flex justify-between mb-2">
                          <div>
                            <h3 className="font-semibold text-lg">
                              Orden #{order._id.slice(-4)}
                            </h3>
                            <p className="text-sm text-gray-500">
                              {formatDate(order.date)}
                            </p>
                          </div>
                          <div className="text-right">
                            <span className="font-bold text-orange-500">
                              ${order.amount.toFixed(2)}
                            </span>
                            <p
                              className={`text-sm ${
                                order.status === "PagoSolicitado"
                                  ? "text-orange-600 font-medium"
                                  : "text-gray-500"
                              }`}
                            >
                              {order.status}
                            </p>
                          </div>
                        </div>
                        <div className="mt-4 space-y-2">
                          {order.items.map((item, index) => (
                            <div
                              key={index}
                              className={`flex justify-between text-sm border-t pt-2 ${
                                order.status === "PagoSolicitado"
                                  ? "border-orange-200 text-gray-700"
                                  : "border-gray-100 text-gray-600"
                              }`}
                            >
                              <div className="flex-1">
                                <p className="font-medium">{item.name}</p>
                                {item.addOnSelected && (
                                  <p className="text-xs text-gray-500">
                                    Opción: {item.addOnSelected}
                                  </p>
                                )}
                                {item.comment && (
                                  <p className="text-xs italic text-gray-500">
                                    Comentario: {item.comment}
                                  </p>
                                )}
                              </div>
                              <span className="ml-4">
                                ${Number(item.price).toFixed(2)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {/* Sección de pago */}
              <div className="fixed bottom-0 left-0 right-0 bg-white shadow-lg border-t border-gray-200 p-4">
                <div className="max-w-2xl mx-auto">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-xl font-bold">Total a Pagar</span>
                    <span className="text-xl font-bold text-orange-500">
                      ${totalToPay.toFixed(2)}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <Button
                      onClick={() => handlePayment("Efectivo")}
                      disabled={processingPayment || totalToPay === 0}
                      className="w-full bg-orange-500 hover:bg-orange-600 text-white disabled:opacity-50"
                    >
                      {processingPayment ? "Procesando..." : "Pago Efectivo"}
                    </Button>
                    <Button
                      onClick={() => handlePayment("Tarjeta")}
                      disabled={processingPayment || totalToPay === 0}
                      className="w-full bg-orange-500 hover:bg-orange-600 text-white disabled:opacity-50"
                    >
                      {processingPayment ? "Procesando..." : "Pago Tarjeta"}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;
